// Keep Stretch's modal, gallery, picker, rerender and quantity components.
// Guard its asynchronous product loads so stale selections cannot be purchased.
(() => {
  const opening = new WeakMap(), selections = new WeakMap();
  const french = () => document.documentElement.lang === 'fr';
  const emit = type => document.documentElement.dispatchEvent(new CustomEvent(type, {bubbles:true}));
  const errorText = () => french() ? 'Impossible de charger ce produit. Réessayez.' : 'This product could not be loaded. Please try again.';
  function clearError(owner) { owner.querySelector('[data-daelivo-quick-buy-error]')?.remove(); }
  function showError(owner, retry) {
    clearError(owner);
    const region = document.createElement('p'); region.dataset.daelivoQuickBuyError = ''; region.className = 'text-error'; region.setAttribute('role','alert');
    region.textContent = errorText();
    if (retry) {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'link';
      button.textContent = french() ? 'Réessayer' : 'Retry'; button.addEventListener('click', retry); region.append(button);
    }
    owner.append(region);
  }
  async function loadContent(url, signal) {
    const response = await fetch(url, {signal, headers:{Accept:'text/html'}});
    if (!response.ok) throw Error('Product request failed');
    const template = new DOMParser().parseFromString(await response.text(),'text/html').getElementById('quick-buy-content');
    if (!template?.content?.firstElementChild) throw Error('Product content missing');
    return template.content.cloneNode(true);
  }
  async function withTimeout(operation) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(),15000);
    try { return await operation(controller.signal); } finally { window.clearTimeout(timer); }
  }
  function controlsBusy(modal, busy) {
    [modal,...modal.controls].forEach(control => control.setAttribute('aria-busy',String(busy)));
  }
  window.DaelivoQuickBuy = {
    async waitForSelection(form) {
      const modal = form.closest('quick-buy-modal[data-daelivo-quick-buy]');
      if (modal) {
        let state;
        do { state = selections.get(modal); await state?.work; } while (selections.get(modal) !== state);
      }
      if (form.dataset.daelivoQuickBuyError === 'true') throw Error(errorText());
      if (form.dataset.daelivoQuickBuyUnavailable === 'true') throw Error(french() ? 'Cette option ne peut pas être commandée.' : 'This option is not available to order.');
    }
  };
  customElements.whenDefined('quick-buy-modal').then(() => {
    const QuickBuy = customElements.get('quick-buy-modal');
    const original = QuickBuy.prototype.show;
    const showNative = Object.getPrototypeOf(QuickBuy.prototype).show;
    QuickBuy.prototype.show = function(...args) {
      if (!this.hasAttribute('data-daelivo-quick-buy')) return original.apply(this,args);
      if (this.open) return Promise.resolve();
      if (opening.has(this)) return opening.get(this);
      const owner = this.controls[0]?.closest?.('product-card') || this.controls[0] || this;
      const work = (async () => {
        clearError(owner); controlsBusy(this,true); emit('theme:loading:start');
        try {
          const fragment = await withTimeout(signal => loadContent(this.getAttribute('product-url'),signal));
          this.replaceChildren(fragment.firstElementChild);
          await showNative.call(this);
          window.Shopify?.PaymentButton?.init();
        } catch { showError(owner); }
        finally { controlsBusy(this,false); emit('theme:loading:end'); opening.delete(this); }
      })();
      opening.set(this,work); return work;
    };
  });
  customElements.whenDefined('variant-picker').then(() => {
    const Picker = customElements.get('variant-picker'), original = Picker.prototype.selectCombination;
    if (!original) return;
    Picker.prototype.selectCombination = function(args) {
      const modal = this.closest('quick-buy-modal[data-daelivo-quick-buy]');
      if (!modal || this.getAttribute('context') !== 'quick_buy') return original.call(this,args);
      const form = document.forms[this.getAttribute('form-id')];
      if (!form) return Promise.resolve();
      const prior = selections.get(modal); prior?.controller.abort();
      const controller = new AbortController(), state = {controller};
      const setPending = busy => {
        form.querySelectorAll('[type="submit"]').forEach(button => { button.disabled = busy; button.setAttribute('aria-busy',String(busy)); });
        form.querySelectorAll('.shopify-payment-button').forEach(button => { button.hidden = busy; });
      };
      clearError(form); form.dataset.daelivoQuickBuyError = 'false'; form.dataset.daelivoQuickBuyUnavailable = 'true'; setPending(true);
      modal.setAttribute('aria-busy','true');
      const timer = window.setTimeout(() => controller.abort(),15000);
      const work = (async () => {
        try {
          const url = new URL(modal.getAttribute('product-url'),window.location.origin);
          url.searchParams.delete('variant'); url.searchParams.set('option_values',args.optionValues.join(','));
          const fragment = await loadContent(url,controller.signal);
          if (selections.get(modal) !== state || !modal.isConnected || !form.isConnected) return;
          const picker = fragment.querySelector(`variant-picker[form-id="${this.getAttribute('form-id')}"]`);
          const variant = JSON.parse(picker?.querySelector('script[data-variant]')?.textContent || '{}');
          if (!variant.id) throw Error('Product variant missing');
          form.dataset.daelivoQuickBuyUnavailable = String(!variant.available);
          const input = form.elements.namedItem('id'); input.value = variant.id; input.dispatchEvent(new Event('change',{bubbles:true}));
          form.dispatchEvent(new CustomEvent('product:rerender',{detail:{htmlFragment:fragment,productChange:false}}));
          form.dispatchEvent(new CustomEvent('variant:change',{bubbles:true,detail:{variant}}));
          form.querySelectorAll('[type="submit"]').forEach(button => { button.disabled = !variant.available; button.removeAttribute('aria-busy'); });
          form.querySelectorAll('.shopify-payment-button').forEach(button => { button.hidden = !variant.available; });
          window.Shopify?.PaymentButton?.init();
        } catch {
          if (selections.get(modal) !== state) return;
          form.dataset.daelivoQuickBuyError = 'true';
          showError(form,() => modal.querySelector('variant-picker')?.selectCombination(args));
        } finally {
          window.clearTimeout(timer);
          if (selections.get(modal) === state) modal.setAttribute('aria-busy','false');
        }
      })();
      state.work = work; selections.set(modal,state); return work;
    };
  });
})();
